// Snapshot Detail Page - View a complete snapshot with all ratings and reflections

import { useEffect, useState } from 'react';
import { useParams, useNavigate, useSearchParams, Link } from 'react-router-dom';
import { snapshotsAPI, commitmentsAPI } from '../api/client';
import { useAuth } from '../context/useAuth';
import SnapshotChangeModal from '../components/SnapshotChangeModal';
import toast from 'react-hot-toast';
import DeltaIndicator from '../components/DeltaIndicator';
import {
  ArrowLeft,
  Calendar,
  Target,
  Heart,
  MessageCircle,
  ChevronDown,
  ChevronUp,
  Bot,
  User as UserIcon,
  Edit,
  CheckCircle,
  Clock,
  Star,
  AlertTriangle,
  Briefcase,
  Printer,
  Trash2,
} from 'lucide-react';
import BrandLogo from '../components/BrandLogo';
import type { Snapshot, SnapshotRating, ChatMessage, Commitment } from '../types';
import {
  SCHEMA_STATE_DIRECTION,
  ratingSortKey,
  isAttention,
  isCelebrate,
  stateColor,
} from '../lib/snapshotFlags';

// Most important area first, then attention → celebrate → rest. Ratings arrive
// in fill-out order and the sort is stable, so ties keep that order.
function orderForReview(ratings: SnapshotRating[], mostImportantName?: string | null): SnapshotRating[] {
  const key = (r: SnapshotRating) =>
    (mostImportantName && r.category_name === mostImportantName ? -1 : ratingSortKey(r.state, r.direction));
  return [...ratings].sort((a, b) => key(a) - key(b));
}

function secondaryViewLabel(categoryName: string): string {
  return categoryName === 'Parenting & Children' ? "Children's view" : "Spouse's view";
}

function visibleTranscript(transcript: ChatMessage[] | null | undefined): ChatMessage[] {
  return (transcript ?? []).filter(
    (message) =>
      (message.role === 'user' || message.role === 'assistant') &&
      typeof message.content === 'string' &&
      message.content.trim().length > 0,
  );
}

export default function SnapshotDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const [snapshot, setSnapshot] = useState<Snapshot | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [expandedRatings, setExpandedRatings] = useState<Set<number>>(new Set());
  // The snapshot owner's active grow goals, for the printed report only.
  const [growGoals, setGrowGoals] = useState<Commitment[]>([]);
  const [changeAction, setChangeAction] = useState<'revise' | 'delete' | null>(null);
  const [changeBusy, setChangeBusy] = useState(false);

  // Arriving with ?print=1 (the card-level print shortcut on the Snapshots
  // page) opens the print dialog as soon as the report has rendered.
  useEffect(() => {
    if (!snapshot || searchParams.get('print') !== '1') return;
    const timer = window.setTimeout(() => {
      window.print();
      setSearchParams({}, { replace: true });
    }, 300);
    return () => window.clearTimeout(timer);
  }, [snapshot, searchParams, setSearchParams]);

  useEffect(() => {
    const fetchSnapshot = async () => {
      if (!id) return;
      try {
        const data = await snapshotsAPI.getSnapshot(parseInt(id));
        setSnapshot(data);
        // Grow goals for the print report. The commitments endpoint already
        // scopes visibility (own goals + goals shared with a facilitator), so
        // filtering by the snapshot owner is safe for both self and
        // facilitator views.
        try {
          const commitments = await commitmentsAPI.getAll();
          setGrowGoals(
            commitments.filter(
              (c: Commitment) =>
                c.user === data.user && (c.status === 'pending' || c.status === 'in_progress'),
            ),
          );
        } catch {
          // Non-blocking: the report simply omits the grow goals section.
        }
      } catch (error) {
        console.error('Error fetching snapshot:', error);
        const source = searchParams.get('from');
        navigate(
          source === 'group'
            ? '/snapshots?tab=group'
            : source === 'accountability'
              ? '/accountability-groups'
              : '/snapshots',
        );
      } finally {
        setIsLoading(false);
      }
    };
    fetchSnapshot();
  }, [id, navigate, searchParams]);

  const toggleRatingExpanded = (ratingId: number) => {
    setExpandedRatings((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(ratingId)) newSet.delete(ratingId);
      else newSet.add(ratingId);
      return newSet;
    });
  };

  // Legacy (v1) score colors.
  const getScoreColor = (score: number) => {
    if (score <= 3) return 'bg-red-500';
    if (score <= 5) return 'bg-yellow-500';
    if (score <= 7) return 'bg-blue-500';
    return 'bg-green-500';
  };

  const getScoreBgColor = (score: number) => {
    if (score <= 3) return 'bg-red-50 border-red-200';
    if (score <= 5) return 'bg-yellow-50 border-yellow-200';
    if (score <= 7) return 'bg-blue-50 border-blue-200';
    return 'bg-green-50 border-green-200';
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-navy"></div>
      </div>
    );
  }

  if (!snapshot) {
    return (
      <div className="text-center py-12">
        <p className="text-gray-500">Snapshot not found</p>
        <Link to="/snapshots" className="text-navy hover:underline mt-2 inline-block">
          Back to Snapshots
        </Link>
      </div>
    );
  }

  const isV2 = snapshot.schema_version === SCHEMA_STATE_DIRECTION;

  // Return shared snapshots to the surface they were opened from.
  const source = searchParams.get('from');
  const backTarget =
    source === 'group'
      ? '/snapshots?tab=group'
      : source === 'accountability'
        ? '/accountability-groups'
        : '/snapshots';

  // Skipped (not-applicable) areas are shown minimally at the bottom, never
  // mixed into the active sections.
  const skippedRatings = isV2 ? snapshot.ratings.filter((r) => r.skipped) : [];
  const activeRatings = isV2 ? snapshot.ratings.filter((r) => !r.skipped) : snapshot.ratings;

  const orderedRatings = isV2
    ? orderForReview(activeRatings, snapshot.most_important_category_name)
    : activeRatings;

  // "Read first" flagged summary: just the attention/celebrate areas, in the
  // same attention → celebrate order used by the full list below.
  const flaggedRatings = isV2
    ? orderedRatings.filter((r) => isAttention(r.state, r.direction) || isCelebrate(r.state, r.direction))
    : [];

  const isOwner = user?.id === snapshot.user;

  const handleChangeConfirm = async (reason: string) => {
    if (!changeAction) return;
    setChangeBusy(true);
    try {
      if (changeAction === 'revise') {
        const draft = await snapshotsAPI.reviseSnapshot(snapshot.id, reason);
        toast.success('Started a new version');
        navigate(`/snapshots/new?draft=${draft.id}`);
      } else {
        await snapshotsAPI.deleteSnapshot(snapshot.id, reason);
        toast.success('Snapshot deleted');
        navigate(backTarget);
      }
    } catch {
      toast.error(
        changeAction === 'revise'
          ? 'Could not start an edit.'
          : 'Could not delete this snapshot.',
      );
    } finally {
      setChangeBusy(false);
      setChangeAction(null);
    }
  };

  return (
    <>
    {/* Paper version: a compact one-page report, only visible when printing. */}
    <SnapshotPrintReport snapshot={snapshot} isV2={isV2} growGoals={growGoals} />

    <div className="max-w-4xl mx-auto space-y-6 print-hidden">
      {/* Header */}
      <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        <div className="flex items-center gap-4 min-w-0">
          <button
            onClick={() => navigate(backTarget)}
            className="p-2 hover:bg-white rounded-lg transition"
            aria-label="Back to snapshots"
          >
            <ArrowLeft size={24} className="text-navy" />
          </button>
          <div>
            <div className="flex items-center gap-3">
              <h1 className="text-2xl font-bold text-navy">{snapshot.month_display}</h1>
              {snapshot.is_finalized ? (
                <span className="inline-flex items-center gap-1 px-2 py-1 bg-green-100 text-green-700 rounded-full text-xs font-medium">
                  <CheckCircle size={12} />
                  Finalized
                </span>
              ) : (
                <span className="inline-flex items-center gap-1 px-2 py-1 bg-yellow-100 text-yellow-700 rounded-full text-xs font-medium">
                  <Clock size={12} />
                  Draft
                </span>
              )}
            </div>
            <div className="flex flex-wrap items-center gap-x-4 gap-y-1 mt-1 text-sm text-gray-500">
              <span className="flex items-center gap-1">
                <Calendar size={14} />
                Created {new Date(snapshot.created_at).toLocaleDateString()}
              </span>
              {!isV2 && snapshot.overall_delta !== null && (
                <span className="flex items-center gap-1">
                  Overall: <DeltaIndicator delta={snapshot.overall_delta} />
                </span>
              )}
            </div>
          </div>
        </div>

        <div className="flex flex-wrap items-center gap-2 sm:flex-nowrap sm:shrink-0">
          <button
            onClick={() => window.print()}
            className="inline-flex items-center gap-2 px-4 py-2 text-navy border border-navy rounded-lg hover:bg-navy hover:text-white transition"
          >
            <Printer size={18} />
            <span className="hidden sm:inline">Print / Save PDF</span>
            <span className="sm:hidden">Print</span>
          </button>
          {isOwner && !snapshot.is_finalized && (
            <Link
              to={`/snapshots/new?draft=${snapshot.id}`}
              className="inline-flex items-center gap-2 px-4 py-2 text-navy border border-navy rounded-lg hover:bg-navy hover:text-white transition"
            >
              <Edit size={18} />
              Continue Editing
            </Link>
          )}
          {isOwner && snapshot.is_finalized && (
            <button
              type="button"
              onClick={() => setChangeAction('revise')}
              className="inline-flex items-center gap-2 px-4 py-2 text-navy border border-navy rounded-lg hover:bg-navy hover:text-white transition"
            >
              <Edit size={18} />
              Edit
            </button>
          )}
          {isOwner && (
            <button
              type="button"
              onClick={() => setChangeAction('delete')}
              className="inline-flex items-center gap-2 px-4 py-2 text-red-600 border border-red-200 rounded-lg hover:bg-red-50 transition"
            >
              <Trash2 size={18} />
              Delete
            </button>
          )}
        </div>
      </div>

      {/* Entrepreneur Pulse (header, v2) */}
      {isV2 && snapshot.owner_experience_label && (
        <div className="bg-white rounded-2xl p-4 shadow-sm flex items-start gap-3">
          <Briefcase size={20} className="text-navy mt-0.5" />
          <div>
            <span className="text-sm text-gray-500">Entrepreneur Pulse</span>
            <p className="font-semibold text-navy">{snapshot.owner_experience_label}</p>
            {snapshot.owner_experience_note && (
              <div className="mt-2">
                <p className="text-xs font-medium text-gray-500">Member Note</p>
                <p className="text-sm text-gray-600">{snapshot.owner_experience_note}</p>
              </div>
            )}
            {snapshot.owner_experience_summary && (
              <div className="mt-2">
                <p className="text-xs font-medium text-gray-500">Ask IF Summary</p>
                <p className="text-sm text-gray-600">{snapshot.owner_experience_summary}</p>
              </div>
            )}
            {visibleTranscript(snapshot.owner_experience_transcript).length > 0 && (
              <TranscriptBlock transcript={visibleTranscript(snapshot.owner_experience_transcript)} />
            )}
          </div>
        </div>
      )}

      {/* Forum-day reading order: most-important → prayer → flagged areas,
          then the full per-category ratings & reflections lower on the page. */}
      {/* 1. Most Important Topic callout */}
      {snapshot.most_important_category_name && (
        <div className="bg-navy text-white rounded-2xl p-6 shadow-sm">
          <h2 className="font-semibold text-lg mb-1 flex items-center gap-2">
            <Star size={20} className="text-gold" />
            Most Important Right Now
          </h2>
          <p className="text-xl font-bold">{snapshot.most_important_category_name}</p>
        </div>
      )}

      {/* 2. Major Prayer Request */}
      {snapshot.major_issue && (
        <div className="bg-white rounded-2xl p-6 shadow-sm border-l-4 border-gold">
          <h2 className="font-semibold text-navy text-lg mb-3 flex items-center gap-2">
            <Heart size={20} className="text-gold" />
            Major Prayer Request
          </h2>
          <p className="text-gray-800 whitespace-pre-wrap">{snapshot.major_issue}</p>
        </div>
      )}

      {/* 3. Flagged areas ("Needs attention" / "Celebrate") surfaced for the group */}
      {isV2 && flaggedRatings.length > 0 && (
        <div className="bg-white rounded-2xl p-6 shadow-sm">
          <h2 className="font-semibold text-navy text-lg mb-4">Areas to Discuss</h2>
          <div className="space-y-2">
            {flaggedRatings.map((rating) => {
              const attention = isAttention(rating.state, rating.direction);
              return (
                <div
                  key={rating.id}
                  className={`flex flex-col items-start gap-2 sm:flex-row sm:items-center sm:justify-between sm:gap-3 p-3 rounded-lg ${
                    attention ? 'bg-red-50' : 'bg-green-50'
                  }`}
                >
                  <div className="flex items-center gap-2 flex-wrap min-w-0">
                    <span className="font-medium text-gray-800">{rating.category_name}</span>
                    {attention ? (
                      <span className="inline-flex items-center gap-1 text-xs font-medium px-2 py-0.5 rounded-full bg-red-100 text-red-700">
                        <AlertTriangle size={12} />
                        Needs attention
                      </span>
                    ) : (
                      <span className="inline-flex items-center gap-1 text-xs font-medium px-2 py-0.5 rounded-full bg-green-100 text-green-700">
                        <Star size={12} />
                        Celebrate
                      </span>
                    )}
                  </div>
                  <div className="flex flex-wrap items-center gap-2 text-xs sm:shrink-0">
                    {rating.state_label && (
                      <span className="px-2 py-0.5 rounded-full bg-white border border-gray-200 text-gray-700 font-medium">
                        {rating.state_label}
                      </span>
                    )}
                    {rating.direction_label && (
                      <span className="px-2 py-0.5 rounded-full bg-white border border-gray-200 text-gray-700 font-medium">
                        {rating.direction_label}
                      </span>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* AI Summary */}
      {snapshot.summary && (
        <div className="bg-white rounded-2xl p-6 shadow-sm">
          <h2 className="font-semibold text-navy text-lg mb-4 flex items-center gap-2">
            <Bot size={20} className="text-gold" />
            AI Summary
          </h2>
          <p className="text-gray-800">{snapshot.summary}</p>
        </div>
      )}

      {/* Your Grow Goals (member-only completion progress) */}
      {snapshot.grow_goal_completion && (
        <div className="bg-white rounded-2xl p-6 shadow-sm">
          <h2 className="font-semibold text-navy text-lg mb-4 flex items-center gap-2">
            <CheckCircle size={20} className="text-gold" />
            Your Grow Goals
          </h2>
          <div className="flex items-center gap-4">
            <span className="text-3xl font-bold text-navy">
              {snapshot.grow_goal_completion.percentage}%
            </span>
            <span className="text-gray-600">
              {snapshot.grow_goal_completion.completed} of {snapshot.grow_goal_completion.total} completed
            </span>
          </div>
          <div className="mt-3 h-2 w-full rounded-full bg-gray-100 overflow-hidden">
            <div
              className="h-full bg-green-500 rounded-full transition-all"
              style={{ width: `${snapshot.grow_goal_completion.percentage}%` }}
            />
          </div>
          <p className="text-xs text-gray-400 mt-2">Only you can see this.</p>
        </div>
      )}

      {/* Foundation */}
      <div className="bg-white rounded-2xl p-6 shadow-sm">
        <h2 className="font-semibold text-navy text-lg mb-4 flex items-center gap-2">
          <Target size={20} />
          Foundation
        </h2>
        <div className="space-y-4">
          {snapshot.purpose_statement && (
            <div>
              <h3 className="text-sm font-medium text-gray-500 mb-1">Purpose Statement</h3>
              <p className="text-gray-800">{snapshot.purpose_statement}</p>
            </div>
          )}
          {!snapshot.purpose_statement && (
            <p className="text-gray-400 italic">No foundation statements provided</p>
          )}
        </div>
      </div>

      {/* 3-5. Ratings (flagged on top for v2) */}
      <div className="bg-white rounded-2xl p-6 shadow-sm">
        <h2 className="font-semibold text-navy text-lg mb-4">Ratings &amp; Reflections</h2>
        <div className="space-y-3">
          {orderedRatings.map((rating) =>
            isV2 ? (
              <StateRatingCard
                key={rating.id}
                rating={rating}
                isExpanded={expandedRatings.has(rating.id)}
                onToggle={() => toggleRatingExpanded(rating.id)}
              />
            ) : (
              <RatingCard
                key={rating.id}
                rating={rating}
                isExpanded={expandedRatings.has(rating.id)}
                onToggle={() => toggleRatingExpanded(rating.id)}
                getScoreColor={getScoreColor}
                getScoreBgColor={getScoreBgColor}
              />
            ),
          )}
        </div>

        {skippedRatings.length > 0 && (
          <div className="mt-4 pt-4 border-t border-gray-100">
            <p className="text-xs font-medium text-gray-400 mb-2">Skipped this month</p>
            <div className="flex flex-wrap gap-2">
              {skippedRatings.map((rating) => (
                <span
                  key={rating.id}
                  className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-gray-50 text-gray-400 text-xs"
                >
                  {rating.category_name}
                </span>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Back Button */}
      <div className="pb-8">
        <button
          onClick={() => navigate(backTarget)}
          className="text-gray-500 hover:text-navy transition flex items-center gap-2 pointer-coarse:py-2.5 pointer-coarse:-my-2.5"
        >
          <ArrowLeft size={18} />
          {source === 'group'
            ? 'Back to Group'
            : source === 'accountability'
              ? 'Back to Accountability Group'
              : 'Back to All Snapshots'}
        </button>
      </div>
    </div>

    <SnapshotChangeModal
      isOpen={changeAction !== null}
      title={changeAction === 'delete' ? 'Delete this snapshot?' : 'Edit this snapshot?'}
      message={
        changeAction === 'delete'
          ? 'This removes the snapshot. Your facilitator will be notified if it was already submitted.'
          : 'Your submitted snapshot stays. We will start a new version you can change, and notify your facilitator.'
      }
      confirmLabel={changeAction === 'delete' ? 'Delete snapshot' : 'Start new version'}
      variant={changeAction === 'delete' ? 'danger' : 'primary'}
      busy={changeBusy}
      onConfirm={handleChangeConfirm}
      onCancel={() => setChangeAction(null)}
    />
    </>
  );
}

// --- One-page print report ---
// Modeled on the original paper "Member Snapshot™" form: name/date header,
// purpose + season goal, major prayer request, a compact table of every
// category with its member note, Jarvis summary, compact conversation, and
// active grow goals.
function SnapshotPrintReport({
  snapshot,
  isV2,
  growGoals,
}: {
  snapshot: Snapshot;
  isV2: boolean;
  growGoals: Commitment[];
}) {
  const unskipped = snapshot.ratings.filter((r) => !isV2 || !r.skipped);
  const active = isV2 ? orderForReview(unskipped, snapshot.most_important_category_name) : unskipped;
  const skipped = isV2 ? snapshot.ratings.filter((r) => r.skipped) : [];

  const youLabel = (r: SnapshotRating) =>
    isV2
      ? [r.state_label, r.direction_label].filter(Boolean).join(' · ') || '—'
      : r.score != null
        ? String(r.score)
        : '—';

  const spouseLabel = (r: SnapshotRating) =>
    isV2
      ? [r.spouse_state_label, r.spouse_direction_label].filter(Boolean).join(' · ')
      : r.spouse_score != null
        ? String(r.spouse_score)
        : '';

  const hasSpouseColumn = active.some((r) => spouseLabel(r));

  const flagLabel = (r: SnapshotRating) =>
    isAttention(r.state, r.direction)
      ? 'Needs attention'
      : isCelebrate(r.state, r.direction)
        ? 'Celebrate'
        : '';

  const frequencyLabel: Record<Commitment['frequency'], string> = {
    one_time: 'One-time',
    daily: 'Daily',
    weekly: 'Weekly',
    biweekly: 'Biweekly',
    monthly: 'Monthly',
  };

  return (
    <div className="snapshot-report">
      <div className="report-masthead">
        <BrandLogo variant="color" hideTagline className="report-logo" />
        <div className="report-masthead-text">
          <p className="report-kicker">Member Snapshot™</p>
          <h1>{snapshot.month_display}</h1>
          <p className="report-who">{snapshot.user_name}</p>
        </div>
      </div>

      {(snapshot.purpose_statement || snapshot.owner_experience_label) && (
        <section>
          {snapshot.purpose_statement && (
            <p>
              <strong>My Purpose Statement:</strong> {snapshot.purpose_statement}
            </p>
          )}
          {snapshot.owner_experience_label && (
            <>
              <p><strong>Entrepreneur Pulse:</strong> {snapshot.owner_experience_label}</p>
              {snapshot.owner_experience_note && <p><strong>Member note:</strong> {snapshot.owner_experience_note}</p>}
              {snapshot.owner_experience_summary && <p><strong>Ask IF summary:</strong> {snapshot.owner_experience_summary}</p>}
            </>
          )}
        </section>
      )}

      {snapshot.major_issue && (
        <section className="report-callout">
          <p>
            <strong>Major Issue for Discussion / Major Prayer Request:</strong>
          </p>
          <p>{snapshot.major_issue}</p>
        </section>
      )}

      <table>
        <thead>
          <tr>
            <th>Category</th>
            <th>You</th>
            {hasSpouseColumn && <th>Family Rating</th>}
            <th></th>
          </tr>
        </thead>
        <tbody>
          {active.map((r) => {
            const mostImportant = r.category_name === snapshot.most_important_category_name;
            return (
              <tr key={r.id}>
                <td>
                  {mostImportant ? '★ ' : ''}
                  <strong>{r.category_name}</strong>
                  {r.scripture_reference && <em> {r.scripture_reference}</em>}
                  {r.rating_text?.trim() && (
                    <p className="report-note">
                      <strong>Member note:</strong> {r.rating_text}
                    </p>
                  )}
                  {r.description?.trim() && (
                    <p className="report-note">
                      <strong>Ask IF summary:</strong> {r.description}
                    </p>
                  )}
                </td>
                <td>{youLabel(r)}</td>
                {hasSpouseColumn && <td>{spouseLabel(r)}</td>}
                <td className={flagLabel(r) === 'Celebrate' ? 'flag-celebrate' : 'flag-attention'}>
                  {flagLabel(r)}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>

      {(visibleTranscript(snapshot.owner_experience_transcript).length > 0
        || active.some((rating) => visibleTranscript(rating.conversation_transcript).length > 0)) && (
        <section>
          <h2>Ask IF Conversations</h2>
          {visibleTranscript(snapshot.owner_experience_transcript).length > 0 && (
            <div className="report-note">
              <strong>Entrepreneur Pulse</strong>
              {visibleTranscript(snapshot.owner_experience_transcript).map((message, index) => (
                <p key={index}>
                  <strong>{message.role === 'user' ? 'Member' : 'Ask IF'}:</strong>{' '}
                  {message.content}
                </p>
              ))}
            </div>
          )}
          {active.map((rating) => {
            const transcript = visibleTranscript(rating.conversation_transcript);
            if (transcript.length === 0) return null;
            return (
              <div key={rating.id} className="report-note">
                <strong>{rating.category_name}</strong>
                {transcript.map((message, index) => (
                  <p key={index}>
                    <strong>{message.role === 'user' ? 'Member' : 'Ask IF'}:</strong>{' '}
                    {message.content}
                  </p>
                ))}
              </div>
            );
          })}
        </section>
      )}

      {growGoals.length > 0 && (
        <section className="report-goals">
          <h2>Grow Goals</h2>
          <ul>
            {growGoals.map((g) => (
              <li key={g.id}>
                {g.description}
                <em>
                  {' '}
                  — {frequencyLabel[g.frequency]}
                  {g.category_name ? ` · ${g.category_name}` : ''}
                </em>
              </li>
            ))}
          </ul>
        </section>
      )}

      <footer className="report-footer">
        <p className="report-footer-brand">Iron Forums · Connect » Sharpen » Grow</p>
        <p>
          {snapshot.most_important_category_name && <>★ = most important right now</>}
          {snapshot.most_important_category_name && skipped.length > 0 && <> · </>}
          {skipped.length > 0 && <>Skipped: {skipped.map((r) => r.category_name).join(', ')}</>}
        </p>
      </footer>
    </div>
  );
}

// --- v2 State/Direction rating card ---
interface StateCardProps {
  rating: SnapshotRating;
  isExpanded: boolean;
  onToggle: () => void;
}

function StateRatingCard({ rating, isExpanded, onToggle }: StateCardProps) {
  const attention = isAttention(rating.state, rating.direction);
  const celebrate = isCelebrate(rating.state, rating.direction);
  const hasDescription = !!(rating.description && rating.description.trim());
  const hasText = !!(rating.rating_text && rating.rating_text.trim());
  const transcript = visibleTranscript(rating.conversation_transcript);
  const hasTranscript = transcript.length > 0;
  const hasContent = hasDescription || hasText || hasTranscript;

  const borderClass = attention
    ? 'bg-red-50 border-red-200'
    : celebrate
    ? 'bg-green-50 border-green-200'
    : 'bg-gray-50 border-gray-200';

  return (
    <div className={`border rounded-xl overflow-hidden transition ${borderClass}`}>
      <button
        onClick={hasContent ? onToggle : undefined}
        className={`w-full p-4 flex flex-col items-stretch gap-2 sm:flex-row sm:items-center sm:justify-between sm:gap-3 ${hasContent ? 'cursor-pointer hover:bg-white/50' : 'cursor-default'}`}
        disabled={!hasContent}
        aria-expanded={isExpanded}
      >
        <div className="flex items-center gap-3 text-left flex-wrap min-w-0">
          <span className={`${stateColor(rating.state)} w-3 h-3 rounded-full flex-shrink-0`} />
          <div>
            <h3 className="font-semibold text-navy">{rating.category_name}</h3>
            {rating.scripture_reference && (
              <p className="text-xs text-gray-500 italic">{rating.scripture_reference}</p>
            )}
          </div>
          {attention && (
            <span className="inline-flex items-center gap-1 text-xs font-medium px-2 py-0.5 rounded-full bg-red-100 text-red-700">
              <AlertTriangle size={12} />
              Needs attention
            </span>
          )}
          {celebrate && (
            <span className="inline-flex items-center gap-1 text-xs font-medium px-2 py-0.5 rounded-full bg-green-100 text-green-700">
              <Star size={12} />
              Celebrate
            </span>
          )}
        </div>

        <div className="flex flex-wrap items-center gap-2 sm:shrink-0">
          {rating.state_label && (
            <span className="px-2 py-0.5 rounded-full bg-white border border-gray-200 text-gray-700 text-xs font-medium">
              {rating.state_label}
            </span>
          )}
          {rating.direction_label && (
            <span className="px-2 py-0.5 rounded-full bg-white border border-gray-200 text-gray-700 text-xs font-medium">
              {rating.direction_label}
            </span>
          )}
          {hasContent && (
            <div className="flex items-center gap-1 text-gray-400 print-hidden ml-auto">
              {hasTranscript && <MessageCircle size={16} className="text-gold" />}
              {isExpanded ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
            </div>
          )}
        </div>
      </button>

      {isExpanded && hasContent && (
        <div className="px-4 pb-4 space-y-4 bg-white/50">
          {(rating.spouse_state_label || rating.spouse_direction_label) && (
            <p className="text-sm text-gray-600">
              <span className="font-medium">
                {secondaryViewLabel(rating.category_name)}:{' '}
              </span>
              {[rating.spouse_state_label, rating.spouse_direction_label].filter(Boolean).join(', ')}
            </p>
          )}
          {hasText && (
            <div className="bg-white rounded-lg p-4 border border-gray-100">
              <h4 className="text-sm font-medium text-gray-500 mb-2">Member Note</h4>
              <p className="text-gray-700 whitespace-pre-wrap">{rating.rating_text}</p>
            </div>
          )}
          {hasDescription && (
            <div className="bg-white rounded-lg p-4 border border-gray-100">
              <h4 className="text-sm font-medium text-gray-500 mb-2 flex items-center gap-2">
                <Bot size={14} className="text-gold" />
                Ask IF Summary
              </h4>
              <p className="text-gray-700">{rating.description}</p>
            </div>
          )}
          {hasTranscript && <TranscriptBlock transcript={transcript} />}
        </div>
      )}
    </div>
  );
}

// --- legacy (v1) score rating card ---
interface RatingCardProps {
  rating: SnapshotRating;
  isExpanded: boolean;
  onToggle: () => void;
  getScoreColor: (score: number) => string;
  getScoreBgColor: (score: number) => string;
}

function RatingCard({ rating, isExpanded, onToggle, getScoreColor, getScoreBgColor }: RatingCardProps) {
  const score = rating.score ?? 0;
  const hasDescription = rating.description && rating.description.trim().length > 0;
  const transcript = visibleTranscript(rating.conversation_transcript);
  const hasTranscript = transcript.length > 0;
  const hasContent = hasDescription || hasTranscript;

  return (
    <div className={`border rounded-xl overflow-hidden transition ${getScoreBgColor(score)}`}>
      <button
        onClick={hasContent ? onToggle : undefined}
        className={`w-full p-4 flex items-center justify-between ${hasContent ? 'cursor-pointer hover:bg-white/50' : 'cursor-default'}`}
        disabled={!hasContent}
        aria-expanded={isExpanded}
      >
        <div className="flex items-center gap-4">
          <div className={`${getScoreColor(score)} text-white font-bold w-10 h-10 rounded-full flex items-center justify-center text-lg`}>
            {rating.score}
          </div>
          <div className="text-left">
            <h3 className="font-semibold text-navy">{rating.category_name}</h3>
            {rating.scripture_reference && (
              <p className="text-xs text-gray-500 italic">{rating.scripture_reference}</p>
            )}
          </div>
        </div>

        <div className="flex items-center gap-3">
          {rating.spouse_score && (
            <div className="text-right">
              <p className="text-xs text-gray-500">
                {secondaryViewLabel(rating.category_name)}
              </p>
              <span className={`${getScoreColor(rating.spouse_score)} text-white font-bold px-2 py-0.5 rounded-full text-sm`}>
                {rating.spouse_score}
              </span>
            </div>
          )}
          {rating.delta !== null && <DeltaIndicator delta={rating.delta} />}
          {hasContent && (
            <div className="flex items-center gap-1 text-gray-400 print-hidden">
              {hasTranscript && <MessageCircle size={16} className="text-gold" />}
              {isExpanded ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
            </div>
          )}
        </div>
      </button>

      {isExpanded && hasContent && (
        <div className="px-4 pb-4 space-y-4 bg-white/50">
          {hasDescription && (
            <div className="bg-white rounded-lg p-4 border border-gray-100">
              <h4 className="text-sm font-medium text-gray-500 mb-2 flex items-center gap-2">
                <Bot size={14} className="text-gold" />
                Ask IF Summary
              </h4>
              <p className="text-gray-700">{rating.description}</p>
            </div>
          )}
          {hasTranscript && <TranscriptBlock transcript={transcript} />}
        </div>
      )}
    </div>
  );
}

function TranscriptBlock({ transcript }: { transcript: ChatMessage[] }) {
  const visibleMessages = visibleTranscript(transcript);
  return (
    <div className="bg-white rounded-lg p-4 border border-gray-100">
      <h4 className="text-sm font-medium text-gray-500 mb-3 flex items-center gap-2">
        <MessageCircle size={14} />
        Conversation
      </h4>
      <div className="space-y-3 max-h-64 overflow-y-auto">
        {visibleMessages.map((msg: ChatMessage, index: number) => (
          <div key={index} className={`flex gap-2 ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}>
            {msg.role === 'assistant' && (
              <div className="w-6 h-6 bg-navy rounded flex items-center justify-center flex-shrink-0">
                <Bot className="text-gold" size={12} />
              </div>
            )}
            <div
              className={`max-w-[80%] p-2 rounded-lg text-sm ${
                msg.role === 'user' ? 'bg-navy text-white' : 'bg-off-white text-gray-800'
              }`}
            >
              {msg.content}
            </div>
            {msg.role === 'user' && (
              <div className="w-6 h-6 bg-gold rounded flex items-center justify-center flex-shrink-0">
                <UserIcon className="text-white" size={12} />
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
